import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { noop } from "../../utils/noop";
/**
 * Context for tracking if Tooltips should immediately render
 * if another tooltip has been shown within a certain
 * duration.
 */

export var TooltipSubscriptions = React.createContext({
  onHideTooltip: noop,
  shouldImmediatelyRenderTooltip: function shouldImmediatelyRenderTooltip() {
    return false;
  },
  cancelUnmountAnimation: noop,
  clearCancelUnmountFn: noop
});
/**
 * Global state for tracking delays for Tooltips
 */

export function TooltipSubscriptionsProvider(_ref) {
  var children = _ref.children,
      _ref$delay = _ref.delay,
      delay = _ref$delay === void 0 ? 700 : _ref$delay; // Ideally we'd keep this as state, but we want to avoid _all_
  // Tooltip subscribers from rerendering any time this global
  // context changes.

  var shouldRenderImmediatelyRef = useRef(false);
  var timerRef = useRef(null);
  var cancelUnmountAnimationFnRef = useRef([]); // When a tooltip is hidden (i.e., when the hide unmount animation
  // is triggered) we assume that all subsequent tooltips which are
  // shown within a specified delay should be rendered immediately,
  // without an animation.
  // To accomplish this we:
  // 1. Change our global shouldRenderImmediately boolean to true.
  // 2. Set a timer, to change our shouldRenderImmediately boolean
  //    to false after a set delay.
  // 3. Store a function which allows subsequent tooltips to
  //    cancel any existing unmounting animations of preceeding
  //    tooltips.

  var onHideTooltip = useCallback(function (id, cancelUnmountAnimationFn) {
    shouldRenderImmediatelyRef.current = true;
    cancelUnmountAnimationFnRef.current = [id, cancelUnmountAnimationFn];
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(function () {
      shouldRenderImmediatelyRef.current = false;
    }, delay);
  }, [delay]); // Cancel our timer when unmounting.

  useEffect(function () {
    return function () {
      return clearTimeout(timerRef.current);
    };
  }, []); // A callback which returns the current shouldRenderImmediately
  // boolean. This is used by a tooltip when a mouse hovers
  // over a tooltip target, and the tooltip needs to determine
  // if it should mount immediately.

  var shouldImmediatelyRenderTooltip = useCallback(function () {
    return shouldRenderImmediatelyRef.current;
  }, []); // A (hacky) strategy which allows tooltips to interrupt
  // and immediately cancel the animations of other exiting
  // tooltips.

  var cancelUnmountAnimation = useCallback(function () {
    if (cancelUnmountAnimationFnRef.current && cancelUnmountAnimationFnRef.current[1]) {
      cancelUnmountAnimationFnRef.current[1]();
    }
  }, []);
  var clearCancelUnmountFn = useCallback(function (id) {
    if (cancelUnmountAnimationFnRef.current[0] === id) {
      cancelUnmountAnimationFnRef.current = [];
    }
  }, []);
  var context = useMemo(function () {
    return {
      onHideTooltip: onHideTooltip,
      shouldImmediatelyRenderTooltip: shouldImmediatelyRenderTooltip,
      cancelUnmountAnimation: cancelUnmountAnimation,
      clearCancelUnmountFn: clearCancelUnmountFn
    };
  }, [cancelUnmountAnimation, clearCancelUnmountFn, onHideTooltip, shouldImmediatelyRenderTooltip]);
  return /*#__PURE__*/React.createElement(TooltipSubscriptions.Provider, {
    value: context
  }, children);
}
/**
 * Access tooltip global state
 */

export function useTooltipContext() {
  return useContext(TooltipSubscriptions);
}